import React, { useMemo, useState } from 'react';

import { Button } from '../../ui/button';
import type { CustomScreenComponentProps } from './types';
import onboardingContent from './onboardingContent.json';
import { Card, CardContent, CardTitle } from 'components/ui/card';
import { getTestId } from 'utils/testUtils';
import {
  CustomRadarChart,
  DEFAULT_RADAR_CHART_DEFINITION_KEY,
  RadarAxisData,
  RadarChartData,
} from 'components/RadarChart';

const AXIS_NICKNAMES: Record<string, string> = onboardingContent.axisNicknames;

export const OnboardingScores: React.FC<CustomScreenComponentProps> = ({
  question,
  metadata,
  isSubmitting,
  onComplete,
}) => {
  const [radarData, setRadarData] = useState<RadarChartData | null>(null);
  const definitionKey =
    typeof metadata.radar_definition_key === 'string' && metadata.radar_definition_key.trim()
      ? metadata.radar_definition_key.trim()
      : DEFAULT_RADAR_CHART_DEFINITION_KEY;
  const todayLabel = useMemo(() => {
    return new Date().toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  const nickname = useMemo(() => {
    if (!radarData) {
      return null;
    }

    const latestValues = radarData.axes.filter(
      (axis): axis is RadarAxisData & { latest_value: number } => axis.latest_value !== null,
    );
    if (latestValues.length === 0) {
      return null;
    }
    const maxValue = Math.max(...latestValues.map((item) => item.latest_value));
    const topAxes = latestValues
      .filter((item) => item.latest_value === maxValue)
      .map((item) => item.key);

    if (maxValue < 7) {
      return 'Capacity Climber';
    }

    if (topAxes.length > 1 && maxValue >= 7) {
      return 'Balanced Beaver';
    }

    const primaryAxis = topAxes[0];
    return AXIS_NICKNAMES[primaryAxis] ?? 'High Performer';
  }, [radarData]);

  const { strengthLabel, growthLabel } = useMemo(() => {
    if (!radarData) {
      return { strengthLabel: null, growthLabel: null };
    }

    const latestValues = radarData.axes.filter(
      (axis): axis is RadarAxisData & { latest_value: number } => axis.latest_value !== null,
    );
    if (latestValues.length === 0) {
      return { strengthLabel: null, growthLabel: null };
    }
    const maxValue = Math.max(...latestValues.map((item) => item.latest_value));
    const minValue = Math.min(...latestValues.map((item) => item.latest_value));

    const strengthAxis = latestValues.find((item) => item.latest_value === maxValue) ?? null;
    const growthAxis = latestValues.find((item) => item.latest_value === minValue) ?? null;

    const formatScore = (value: number) => {
      const rounded = Number(value.toFixed(2));
      return rounded % 1 === 0 ? String(rounded) : rounded.toString();
    };

    return {
      strengthLabel:
        strengthAxis !== null
          ? `${strengthAxis.label} (score: ${formatScore(maxValue)})`
          : null,
      growthLabel:
        growthAxis !== null
          ? `${growthAxis.label} (score: ${formatScore(minValue)})`
          : null,
    };
  }, [radarData]);

  const summaryItems = [
    {
      label: 'Strength Area',
      colorClass: 'border-foundation-purplepurple-500',
      value: strengthLabel,
    },
    {
      label: 'Growth Area',
      colorClass: 'border-foundation-orange-500',
      value: growthLabel,
    },
  ];

  return (
    <Card
        {...getTestId(`question-card-${question.id}`)}
        padding="none"
        className="w-full max-w-[53.125rem]"
    >
        <CardTitle className="px-5 pt-5 sm:px-10" title={onboardingContent.scores.title}/>
        {nickname ? (
            <div className="px-5 pb-4 text-foreground font-headings-h3 font-[var(--headings-h1-font-weight)] text-[length:var(--headings-h3-font-size)] tracking-[var(--headings-h1-letter-spacing)] leading-[var(--headings-h2-line-height)] [font-style:var(--headings-h1-font-style)] sm:px-10">
                We see you, <span className='font-medium'>{nickname}</span>
            </div>
        ) : null}
        <CardContent variant="userResponse" padding="large" className="min-w-0 overflow-hidden p-4 pb-20 sm:p-8">
            <div className="inline-flex h-10 items-center justify-center gap-6 px-4 py-2 rounded-lg border border-solid border-foundation-base-darkbase-dark-100">
                <time className="text-center font-semibold text-foreground text-sm tracking-[-0.28px] leading-5 whitespace-normal">
                    {todayLabel}
                </time>
            </div>
            <CustomRadarChart
              definitionKey={definitionKey}
              onDataLoaded={setRadarData}
              retryUntilComplete
            />
            <div className="flex w-full flex-col items-start gap-4 pr-[9.5rem] sm:pr-0">
                {summaryItems.map(({ label, colorClass, value }) => (
                    <div key={label} className="flex min-w-0 items-start gap-4">
                        <div className="inline-flex items-center gap-2.5 px-0 py-1.5">
                            <div className={`w-2 h-2 rounded-[48px] border border-solid ${colorClass}`} />
                        </div>
                        <div className="min-w-0 font-normal text-sm leading-[14px] tracking-[-0.28px]">
                            <span className="font-semibold tracking-[-0.04px] leading-5">
                            {label}:
                            <br />
                            </span>
                            <span className="tracking-[-0.04px] leading-5">
                            {value ?? 'Not available'}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            {onboardingContent.scores.summary}
        </CardContent>
        <div className='p-6'>
            <Button onClick={() => onComplete()} disabled={isSubmitting} type="button" variant="secondary" size='auto'>
                Continue
            </Button>
        </div>
    </Card>
  );
};
